import React from "react";

import { Card } from "../../../components/Card";
import useSWR from "swr";
import API_ENDPOINTS from "../../../apis/endpoints";
import {
    Box,
    Checkbox,
    CircularProgress,
    FormControl,
    InputLabel,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Select,
    Tab,
    Tabs,
} from "@mui/material";
import { styled } from "@mui/styles";
import { useTheme } from "@mui/material/styles";

import LastRefreshed from "../LastRefreshed";
import AIGen from "./aIGen/AIGen";
import OverallGalleri5 from "./overallGalleri5/OverallGalleri5";
import SocialIntelligence from "./socialIntelligence/SocialIntelligence";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 10.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

const StyledTabs = styled(Tabs)(({ theme }) => ({
    borderBottom: `1px solid ${theme.palette.neutral[200]}`,
    "& .MuiTabs-indicator": {
        backgroundColor: theme.palette.secondary[500],
    },
}));

const StyledTab = styled(Tab)(({ theme }) => ({
    textTransform: "none",
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: theme.typography.pxToRem(15),
    marginRight: theme.spacing(1),
    "&.Mui-selected": {
        color: theme.palette.secondary[500],
    },
}));

function a11yProps(index) {
    return {
        id: `simple-tab-${index}`,
        "aria-controls": `simple-tabpanel-${index}`,
    };
}

const tabs = [
    { id: 0, title: "Overall Galleri5" },
    { id: 1, title: "Social Intelligence" },
    { id: 2, title: "AI Gen" },
];

export default function Gallery5() {
    const {
        data: weekDataSet,

        isLoading,
    } = useSWR(`${API_ENDPOINTS.G5_WEEKS}`);

    const {
        data: weekData,
        months: monthData,
        refreshed_at,
    } = weekDataSet ?? {};

    if (isLoading) {
        return <CircularProgress />;
    }

    return (
        <WeekDisplay
            weeks={weekData}
            refreshed_at={refreshed_at}
            months={monthData}
        />
    );
}

function WeekDisplay({ weeks: _weeks, months, refreshed_at }) {
    const theme = useTheme();

    const [filterBy, setFilterBy] = React.useState("weeks"); // weeks or months

    const weeks = React.useMemo(() => {
        return filterBy === "weeks" ? _weeks : months;
    }, [filterBy, _weeks, months]);

    const [selectedWeeks, setSelectedWeeks] = React.useState(
        weeks.slice(-12).map((week) => week.key)
    );
    const [selectedMonths, setSelectedMonths] = React.useState(
        months.slice(-3).map((month) => month.key)
    )

    React.useEffect(() => {
        setSelectedWeeks(weeks.slice(-12).map((week) => week.key));
        setSelectedMonths(months.slice(-3).map((month) => month.key));
    }, [filterBy, weeks]);

    const [value, setValue] = React.useState(0);

    const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    const handleWeekChange = (event) => {
        if(filterBy === "weeks")
        setSelectedWeeks(event.target.value);
        else
        setSelectedMonths(event.target.value);
    };

    return (
        <Card>
            <div className="header-section">
                <Box
                    sx={{
                        borderBottom: 1,
                        borderColor: theme.palette.neutral[200],
                    }}
                >
                    <StyledTabs
                        value={value}
                        onChange={handleChange}
                        aria-label="BBS tabs"
                    >
                        {tabs.map((tab, index) => (
                            <StyledTab
                                key={tab.id}
                                label={tab.title}
                                {...a11yProps(index)}
                            />
                        ))}
                    </StyledTabs>
                </Box>
                {/* Filter by weeks or months */}
                <FormControl sx={{ m: 1, ml: "auto", width: 140 }}>
                    <InputLabel id="filter-by-label">Filter By</InputLabel>
                    <Select
                        labelId="filter-by-label"
                        id="filter-by"
                        value={filterBy}
                        onChange={(e) => setFilterBy(e.target.value)}
                        input={<OutlinedInput label="Filter By" />}
                    >
                        <MenuItem value="weeks">Weeks</MenuItem>
                        <MenuItem value="months">Months</MenuItem>
                    </Select>
                </FormControl>
                {/* Week Selector */}
                <FormControl sx={{ m: 1, width: 300 }}>
                    <InputLabel id="week-multi-select-label">
                        {filterBy === "weeks" ? "Weeks" : "Months"}
                    </InputLabel>
                    <Select
                        labelId="week-multi-select-label"
                        id="week-multi-select"
                        multiple
                        value={filterBy === "weeks" ? selectedWeeks : selectedMonths}
                        onChange={handleWeekChange}
                        input={<OutlinedInput label="All Weeks" />}
                        renderValue={(selected) =>
                            (weeks.length === selectedWeeks.length && filterBy === "weeks") || (weeks.length === selectedMonths.length && filterBy === "months")
                                ? "All " +
                                (filterBy === "weeks" ? "Weeks" : "Months")
                                : filterBy === "weeks" ?
                                    weeks
                                        ?.filter((week) =>
                                            selected.includes(week.key)
                                        )
                                        .map((week) => week.name)
                                        .join(", ")
                                    : months
                                        ?.filter((month) =>
                                            selected.includes(month.key)
                                        )
                                        .map((month) => month.name)
                                        .join(", ")
                        }
                        MenuProps={MenuProps}
                    >
                        {weeks?.map((week) => (
                            <MenuItem key={week.key} value={week.key}>
                                <Checkbox
                                    checked={
                                        filterBy === "weeks" ? selectedWeeks.indexOf(week.key) > -1 : selectedMonths.indexOf(week.key) > -1
                                    }
                                />
                                <ListItemText primary={week.name} />
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </div>

            {/* Pass selected weeks and active tab index */}
            <SelectedWeeks weeks={filterBy === "weeks" ? selectedWeeks : selectedMonths} type={filterBy} tab={value} />

            <LastRefreshed date={refreshed_at} />
        </Card>
    );
}

// SelectedWeeks component to display content based on active tab
function SelectedWeeks({ weeks, type, tab }) {
    const TabList = [OverallGalleri5, SocialIntelligence, AIGen]; // Map tabs to components

    const ActiveTab = TabList[tab]; // Get active tab component

    const {
        data: G5Data,
        error: G5Error,
        isLoading: academyIsLoading,
    } = useSWR(`${API_ENDPOINTS.G5_DATA}?${type}=${weeks.join(",")}`);

    if (academyIsLoading) {
        return <CircularProgress />;
    }

    // Render active tab component with data
    return (
        <div className="cards-container">
            {<ActiveTab data={G5Data} type={type} />}
        </div>
    );
}
